import { AmountInterface } from '../../../../layout/interfaces/amount-interface';
import { RateInterface } from '../../../../layout/interfaces/rate-interface';
import { ActioncodeInterface } from '../../../../shared/entities/bookingParameters';
import { Shop } from '../../../../shared/entities/shop';
import { LegacyMoneyDto } from '../../generated';
import { CartInterface, InsuranceType, RegisterData } from '../cart';
import { Person } from './person';
import { Product } from './product';

export class Cart {
  shopId: number;
  shop?: Shop;
  persons: Person[] = [];
  selectedPerson?: Person;
  startDate?: Date;
  endDate?: Date;
  promotionCode?: string;
  couponCode?: string;
  euroBasedRates?: RateInterface;
  totalPaidOnlineAfterCoupons?: LegacyMoneyDto;
  fee?: LegacyMoneyDto;
  promotionCodeReduction: LegacyMoneyDto;
  discountAbsolute?: LegacyMoneyDto;
  totalPaidOnlineBothTypes?: {
    down: LegacyMoneyDto;
    full: LegacyMoneyDto;
  };
  totalPaidCash?: LegacyMoneyDto;
  totalDeductedFromCoupons: LegacyMoneyDto;
  discountPercentage: string;
  averageQuality = 0;
  coupon?: ActioncodeInterface;

  // fields needed on checkout
  usesBindingData = true;
  paymentTypeChoiceAvailable?: boolean;
  isDownPayment = false;

  cancellationSelected = false; // flexi
  insuranceSelected = false; // safety
  insurancePlusSelected = false; // safety+

  marketingOptIn = 1;
  virtualShopCompanyId = 0; // this is literally the shops company id. (null if not virtual)
  virtualShopTownName = ''; // should definitely be on shop
  virtualShopParentTownName = ''; // should be on shop
  vsEquipmentToLocation = false;
  isVirtualShop = false; // can be checked on shop
  // (register data / personal details)

  // protect yourself prices and "is available at all"
  isGuarantyBookable?: boolean;
  useGuarantyInterface?: boolean; // I literally have no Idea what that does next to isGuarantyBookable.
  potentialCancellationInsuranceRaw?: AmountInterface; // flexi cancellation of equipment up to day before rental
  potentialGuarantyRaw?: AmountInterface; // dmg & theft protecc (addon on equipment :3)
  potentialInsuranceRaw?: AmountInterface; // personal injury, rescue heli fees
  potentialInsurancePlusRaw?: AmountInterface; // personal injury, rescue heli fees + refunds of equip, lift pass, ski course days,
  isCustomerPhoneNumberMandatory = false; // TODO: is this only for skilivery?
  registerData: RegisterData = {
    firstname: '',
    surname: '',
    customerCountry: '',
    phone: '',
    accommodation_name: '',
    accommodation_address: '',
    email: ''
  };
  // fields needed on checkout end

  getDurationInDays(): number {
    // calculate number of days between start and enddate
    let days = Math.round(
      Math.abs((this.startDate.getTime() - this.endDate.getTime()) / (24 * 60 * 60 * 1000))
    );
    days += 1;
    if (days === 0) {
      days = 1;
    }

    return days;
  }

  /**
   * Used to update the query params for a cart on changes. On page load, it will take those
   * params and formulate a modyfiable cart in frontend
   * @returns a url friendly representation of a cart
   */
  toQueryParams(): string | { [key: string]: string | number | number[] } {
    const params = {
      promotionCode: this.promotionCode,
      couponCode: this.couponCode,
      persons: this.persons.map(pers => {
        return {
          products: pers.products,
          age: pers.age,
          weight: pers.weight,
          height: pers.height,
          shoe_size: pers.shoe_size,
          skill: pers.skill
        };
      }),
      insurances: this.insurancesToParams()
    };
    return JSON.stringify(params);
  }

  /**
   * returns global insurance items as InsurancyType array for query params
   * @returns
   */
  private insurancesToParams(): { definitionId: number }[] {
    const insurances = [];
    if (this.cancellationSelected) {
      insurances.push(1);
    }
    if (this.insuranceSelected) {
      insurances.push(2);
    }
    if (this.insurancePlusSelected) {
      insurances.push(3);
    }
    return insurances;
  }

  constructor(cartInterface?: CartInterface, shop?: Shop, products?: Product[]) {
    this.mapDataFrom(cartInterface, shop, products);
  }

  mapDataFrom(cartInterface?: CartInterface, shop?: Shop, products?: Product[]) {
    if (cartInterface) {
      this.persons = [];
      cartInterface.persons.forEach(p => {
        const person = new Person();
        Object.keys(p).forEach(key => {
          person[key] = p[key];
        });
        if (person.age !== undefined && shop) {
          person.ageInterval = shop.ageIntervals.find(ai => ai.start <= person.age && person.age <= ai.end);
        }
        this.persons.push(person);

        p.products.forEach(personProductIds => {
          const product = products.find(products => products.definitionId === personProductIds.definitionId);
          if (product) {
            // Only keep the selected addons
            const selectedAddons = personProductIds.addons
              .map(addonId => product.addons.find(a => a.definitionId === addonId))
              .filter(a => a !== undefined)
              .map(a => ({ ...a, isSelected: true }));

            // Create a new product with only selected addons
            const clonedProduct = new Product({
              ...product,
              addons: selectedAddons
            });

            person.productEntities.push(clonedProduct);
          }
        });
      });
      this.selectedPerson = this.persons[0];
      this.startDate = new Date(cartInterface.startDate);
      this.endDate = new Date(cartInterface.endDate);
      this.promotionCode = cartInterface.promotionCode?.trim();
      this.couponCode = cartInterface.couponCode?.trim();
      this.shopId = +shop?.id;
      this.shop = shop;
      this.cancellationSelected = cartInterface.insurances?.includes(InsuranceType.flexi);
      this.insurancePlusSelected = cartInterface.insurances?.includes(InsuranceType.insurancePlus);
      this.insuranceSelected =
        cartInterface.insurances?.includes(InsuranceType.insurance) && !this.insurancePlusSelected;
    }
  }

  getPaymentOptions(forCurrency: string): {
    down: AmountInterface;
    cash: AmountInterface;
  } {
    // split amount into {down} online and shop {cash} amount.
    // !: can't pay for all items in the shop! What if you cancel and you only partially paid for cancel insurance?
    const splittableAmount = this.getTotalOnlinePrice(forCurrency, { skipInsurances: true }).amount; // shop equipment that can be split
    let nonSplitableAmount = 0; // alpy stuff/insurances, service fee and everything the shop doesn't sell and must be paid online

    // TODO: find out if cancellation, insurance and insurance+ are splittable or not
    if (this.cancellationSelected) {
      nonSplitableAmount += Math.round(
        this.potentialCancellationInsuranceRaw.amount * +this.euroBasedRates.rates[forCurrency]
      );
    }
    if (this.insuranceSelected) {
      nonSplitableAmount += Math.round(
        this.potentialInsuranceRaw.amount * +this.euroBasedRates.rates[forCurrency]
      );
    }
    if (this.insurancePlusSelected) {
      nonSplitableAmount += Math.round(
        this.potentialInsurancePlusRaw.amount * +this.euroBasedRates.rates[forCurrency]
      );
    }

    const onlineSplitAmount = Math.round(splittableAmount * this.getDownpaymentPercentage());

    return {
      down: { amount: onlineSplitAmount + nonSplitableAmount, currency: { code: forCurrency, rates: null } },
      cash: { amount: splittableAmount - onlineSplitAmount, currency: { code: forCurrency, rates: null } }
    };
  }

  getDownpaymentPercentage(): number {
    if (this.totalPaidOnlineBothTypes.full.amount === 0) {
      return 0;
    }
    return this.totalPaidOnlineBothTypes.down.amount / this.totalPaidOnlineBothTypes.full.amount;
  }

  getTotalOnlinePrice(
    forCurrency: string,
    options: {
      skipCouponReduction?: boolean;
      skipInsurances?: boolean;
      skipFee?: boolean;
    } = {}
  ): AmountInterface {
    const { skipCouponReduction = false, skipInsurances = false, skipFee = false } = options;

    let amount = 0;

    this.persons.forEach(person => {
      amount += person.getTotalOnlinePrice(this.getDurationInDays(), this.euroBasedRates, forCurrency).amount;
    });

    // Add fee
    if (!skipFee && this.fee) {
      amount += Math.round(this.fee.amount * this.euroBasedRates.rates[forCurrency]);
    }

    // Add insurances
    if (!skipInsurances) {
      if (this.cancellationSelected && this.potentialCancellationInsuranceRaw?.amount) {
        amount += Math.round(
          this.potentialCancellationInsuranceRaw.amount * this.euroBasedRates.rates[forCurrency]
        );
      }
      if (this.insuranceSelected) {
        amount += Math.round(this.potentialInsuranceRaw.amount * this.euroBasedRates.rates[forCurrency]);
      }
      if (this.insurancePlusSelected) {
        amount += Math.round(this.potentialInsurancePlusRaw.amount * this.euroBasedRates.rates[forCurrency]);
      }
    }

    // Apply coupon reduction
    if (!skipCouponReduction && this.coupon !== undefined) {
      amount = Math.max(
        amount - Math.round(this.coupon.euroBalanceRaw * this.euroBasedRates.rates[forCurrency]),
        0
      );
    }

    return {
      amount,
      currency: { code: forCurrency, rates: {} }
    };
  }

  getTotalShopPrice(forCurrency: string): AmountInterface {
    let amount = 0;
    this.persons.forEach(person => {
      amount += person.getTotalShopPrice(this.getDurationInDays(), this.euroBasedRates, forCurrency).amount;
    });
    return { amount: amount, currency: { code: forCurrency, rates: {} } };
  }

  getDiscountAmount(forCurrency: string): AmountInterface {
    return {
      amount:
        this.getTotalShopPrice(forCurrency).amount -
        this.getTotalOnlinePrice(forCurrency, {
          skipFee: true,
          skipCouponReduction: true,
          skipInsurances: true
        }).amount,
      currency: { code: forCurrency, rates: {} }
    };
  }

  /**
   * Tells you if childFree should be active for the given person (true) or not (false).
   * Takes into consideration the equipment requirement of the adults, not if the given person
   * has equipment that is for free.
   *
   * @param person
   * @returns boolean if person is for free or not
   */
  isChildFreeActiveFor(persons: Person[], person: Person): boolean {
    const numberOfEligibleAdults = persons.filter(
      p => p.isAdult && p.hasEquipmentEligibleForChildFree()
    ).length;

    // if shop has free child equipment offer
    // AND person age is within the free age limit
    // AND enough adults have equipment assigned that allows free child equip
    // AND the index of the person is one of the eligible "seats" by:
    // making sure the kid(s) with the highest total are getting discounted
    // TODO: Find out, if the kid equip price is based on shop OR on online 'before' child free price
    if (
      this.shop?.hasFreeChildren &&
      person.age <= this.shop?.childrenFreeUntilAge &&
      numberOfEligibleAdults >= this.shop?.requiredAdultsForFreeChildren &&
      persons
        .filter(p => p.age <= this.shop?.childrenFreeUntilAge)
        .sort((a, b) => {
          const aPrice = a.getTotalShopPrice(this.getDurationInDays(), this.euroBasedRates, 'EUR').amount;
          const bPrice = b.getTotalShopPrice(this.getDurationInDays(), this.euroBasedRates, 'EUR').amount;
          if (aPrice > bPrice) {
            return -1;
          } else if (aPrice == bPrice) {
            return 0;
          }
          return 1;
        })
        .indexOf(person) +
        1 <=
        Math.floor(numberOfEligibleAdults / this.shop?.requiredAdultsForFreeChildren) *
          this.shop?.freeChildrenPerAdults
    ) {
      return true;
    }

    return false;
  }

  /**
   * finds out if number of people meets or exceeds group discount
   */
  wouldGroupDiscountApply(
    numberOfPeople: number = 0
  ): { type: string; criterion: number; percentage: number } | null {
    if (!Array.isArray(this.shop.discountPromotions)) {
      const groupDiscount = this.shop.discountPromotions?.group.find(
        disc => disc.criterion <= this.persons.length + numberOfPeople
      );
      if (groupDiscount) {
        return groupDiscount;
      }
    }
    return null;
  }

  get startDateString(): string {
    if (this.startDate) {
      return `${this.startDate.getFullYear()}-${(this.startDate.getMonth() + 1).toString().padStart(2, '0')}-${this.startDate.getDate().toString().padStart(2, '0')}`;
    }
  }

  get endDateString(): string {
    if (this.endDate) {
      return `${this.endDate.getFullYear()}-${(this.endDate.getMonth() + 1).toString().padStart(2, '0')}-${this.endDate.getDate().toString().padStart(2, '0')}`;
    }
  }

  /**
   * any persons products has an addon that is an insurance?
   * returns true if so, false if not
   */
  get hasAlpinGuarantyItems(): boolean {
    return this.persons.some(person =>
      person.productEntities.some(product => product.addons.some(addon => addon.definitionId === 3))
    );
  }
  /**
   * returns you a % number how much this code reduced the price
   * code reduction 10€ => 10€ form total 100€ => xx%
   * @param currency
   * @returns
   */
  discountPercentageForPromoCode(currency: string) {
    const total = this.getTotalOnlinePrice(currency, { skipCouponReduction: true }).amount;
    const reduction = Math.round(this.promotionCodeReduction.amount * this.euroBasedRates.rates[currency]);
    return Math.round((total / (total - reduction) - 1) * 100);
  }

  allPersonsHaveItems() {
    return !this.persons.some(p => p.products.length === 0);
  }

  meetsMinimumTurnoverFor(shop: Shop, currency = 'EUR'): boolean {
    if (!this.euroBasedRates) {
      return false;
    }
    return (
      Math.round(shop.minOnlineTurnoverRaw * this.euroBasedRates.rates[currency]) <=
      this.getTotalOnlinePrice(currency, { skipCouponReduction: true }).amount
    );
  }
}
